import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardBody, Collapse, Button, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { CoreAlvisImportConfiguration, AlvisSensitiveFielsOptions } from '../../../../../../models/CoreAlvisModels';
import AlvisSchools from './AlvisSchools';
import ModalComponent from '../../../../../misc/ModalComponent';
import EnumInput from '../../../../../misc/EnumInput';
import StatusCode from '../../../../../../util/StatusCode';
import Loading from '../../../../../misc/Loading';
import InstanceClient from '../../../../../../services/InstanceClient';


export const validator = (configuration: CoreAlvisImportConfiguration, active: boolean) => {
    const validation: string[] = [];

    if (active) {
        if (!configuration.sftpHostname || configuration.sftpHostname === '') {
            validation.push('sftpHostname');
        }

        if (!configuration.sftpUsername || configuration.sftpUsername === '') {
            validation.push('sftpUsername');
        }

        if (!configuration.sftpPassword || configuration.sftpPassword === '') {
            validation.push('sftpPassword');
        }
    }

    if (typeof configuration.startDateOffset !== 'number' || configuration.startDateOffset < 0) {
        validation.push('startDateOffset');
    }

    return validation;
}

const schoolUnitRemoved = (originalConfiguration: CoreAlvisImportConfiguration, updatedConfiguration: CoreAlvisImportConfiguration) => {
    return originalConfiguration.alvisImportSchoolUnits.some(
        existing => updatedConfiguration.alvisImportSchoolUnits && !updatedConfiguration.alvisImportSchoolUnits.some(u => u.id === existing.id)
    );
}

export const warning = (originalConfiguration: CoreAlvisImportConfiguration, updatedConfiguration: CoreAlvisImportConfiguration) => {
    const warningMessages = [];

    if (schoolUnitRemoved(originalConfiguration, updatedConfiguration)) {
        warningMessages.push('Om du tar bort skolor kommer data kopplad till dessa att inaktiveras');
    }

    return warningMessages;
}


interface AlvisImportConfigurationProps {
    disabled: boolean
    updatePart: (part: Partial<CoreAlvisImportConfiguration>) => void
    configuration: CoreAlvisImportConfiguration
    active: boolean
    onValidChange: (arg: boolean) => void 
}


const AlvisImportConfiguration = (props: AlvisImportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);
    const [resetFingerprintModal, setResetFingerprintModal] =
        useState<({
            isOpen: boolean,
            reason: string | null,
            operationStatus: {
                code: StatusCode,
                error?: string | undefined
            }
        })>({ isOpen: false, reason: null, operationStatus: { code: StatusCode.NONE, error: undefined } });

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    const closeResetFingerprintModal = (id: string, confirmed: boolean) => {
        if (confirmed) {
            setResetFingerprintModal({
                ...resetFingerprintModal,
                operationStatus: {
                    code: StatusCode.PENDING
                }
            });

            const body = { reason: resetFingerprintModal.reason };

            InstanceClient.post(`/api/core/alvisimportconfiguration/${id}/clearsftpfingerprint`, body)
                .then(_ => setResetFingerprintModal({
                    isOpen: false,
                    reason: null,
                    operationStatus: {
                        code: StatusCode.COMPLETE
                    }
                }))
                .catch(error =>
                    setResetFingerprintModal({
                        ...resetFingerprintModal,
                        operationStatus: {
                            code: StatusCode.ERROR,
                            error,
                        }
                    }));
        } else {
            setResetFingerprintModal({
                isOpen: false,
                reason: null,
                operationStatus: {
                    code: StatusCode.COMPLETE
                }
            })
        }
    }

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.AlvisImport.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <FormGroup>
                            <Label for="sftpHostname">{t('alvisImport.sftpHostname.label', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="sftpHostname"
                                id="sftpHostname"
                                value={props.configuration.sftpHostname || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ 'sftpHostname': e.target.value })}
                                invalid={validation.indexOf('sftpHostname') !== -1}
                                autoComplete="off"
                                placeholder={t('alvisImport.sftpHostname.placeholder', { ns: 'configuration' })}
                            />
                            <FormFeedback>{t('alvisImport.sftpHostname.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                        <FormGroup>
                            <Label for="sftpUsername">{t('alvisImport.sftpUsername.label', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="sftpUsername"
                                id="sftpUsername"
                                value={props.configuration.sftpUsername || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ 'sftpUsername': e.target.value })}
                                invalid={validation.indexOf('sftpUsername') !== -1}
                                autoComplete="off"
                                placeholder={t('alvisImport.sftpUsername.placeholder', { ns: 'configuration' })}
                            />
                            <FormFeedback>{t('alvisImport.sftpUsername.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                        <FormGroup>
                            <Label for="sftpPassword">{t('alvisImport.sftpPassword.label', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="sftpPassword"
                                id="sftpPassword"
                                value={props.configuration.sftpPassword || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ 'sftpPassword': e.target.value })}
                                invalid={validation.indexOf('sftpPassword') !== -1}
                                autoComplete="off"
                                placeholder={t('alvisImport.sftpPassword.placeholder', { ns: 'configuration' })}
                            />
                            <FormFeedback>{t('alvisImport.sftpPassword.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                        <FormGroup>
                            <div className="edit_module">
                                <Button
                                    className="flowsync-btn"
                                    disabled={props.disabled}
                                    onClick={() => setResetFingerprintModal({ ...resetFingerprintModal, isOpen: true })}
                                >{t('alvisImport.sftpFingerprint.buttonText', { ns: 'configuration' })}</Button>
                            </div>
                        </FormGroup>
                        <ModalComponent
                            isOpen={resetFingerprintModal.isOpen}
                            toggleModal={() => closeResetFingerprintModal(props.configuration.id, false)}
                            header={t('alvisImport.sftpFingerprint.modalHeader', { ns: 'configuration' })}
                            size="xl"
                            disabled={resetFingerprintModal.operationStatus.code === StatusCode.PENDING}
                            update={() => closeResetFingerprintModal(props.configuration.id, true)}
                            confirm={t('alvisImport.sftpFingerprint.confirmReset', { ns: 'configuration' })}
                            cancel={t('action.cancel', { ns: 'common' })}
                        >
                            <p>{t('alvisImport.sftpFingerprint.description', { ns: 'configuration' })}</p>
                            <FormGroup>
                                <Label for="resetFingerprintReason">{t('alvisImport.sftpFingerprint.resetReasonLabel', { ns: 'configuration' })}</Label>
                                <Input
                                    type="text"
                                    id="resetFingerprintReason"
                                    disabled={resetFingerprintModal.operationStatus.code === StatusCode.PENDING}
                                    value={resetFingerprintModal.reason ?? ''}
                                    onChange={(e) => setResetFingerprintModal({ ...resetFingerprintModal, reason: e.target.value })}
                                />
                                {resetFingerprintModal.operationStatus.code === StatusCode.PENDING && <Loading />}
                                {resetFingerprintModal.operationStatus.code === StatusCode.ERROR && <p style={{ color: 'red' }}>{t('alvisImport.sftpFingerprint.resetFailed', { ns: 'configuration' })}</p>}
                            </FormGroup>
                        </ModalComponent>

                        <EnumInput
                            title={t('alvisImport.sensitiveFields.title', { ns: 'configuration' })}
                            name='includeSensitiveFields'
                            value={props.configuration.includeSensitiveFields}
                            options={AlvisSensitiveFielsOptions.map(key => ({
                                value: key,
                                text: t(`alvisImport.sensitiveFields.options.${key}`, { ns: 'configuration' })
                            }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            multi={true}
                        />

                        <FormGroup>
                            <h5>{t('alvisImport.readAhead.header', { ns: 'configuration' })}</h5>
                            <Label for="startDateOffset">{t('alvisImport.readAhead.label', { ns: 'configuration' })}</Label>
                            <Input
                                type="number"
                                name="startDateOffset"
                                id="startDateOffset"
                                placeholder={t('alvisImport.readAhead.placeholder', { ns: 'configuration' })}
                                value={props.configuration.startDateOffset !== undefined || props.configuration.startDateOffset !== null ? props.configuration.startDateOffset : ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ 'startDateOffset': parseInt(e.target.value, 10) })}
                                required
                                invalid={validation.indexOf('startDateOffset') !== -1} />
                            <FormFeedback>{t('alvisImport.readAhead.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>

                        <br></br>
                        <h5>{t('alvisImport.schools.title', { ns: 'configuration' })}</h5>

                        <AlvisSchools
                            name="alvisImportSchoolUnits"
                            schoolUnits={props.configuration.alvisImportSchoolUnits}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            validation={validation}
                        />

                    </div>
                </CardBody>
            </Collapse>
        </Card>
    )
}

export default AlvisImportConfiguration;
